// scanReport — the RICH scan-report shape (zeus-api scan-worker output), the
// superset behind the tabbed MCP Scan report. The stored console shape in
// scans.ts is a narrowed subset; this module models the full artifact the
// scan worker emits (lifecycle stages, adjudicator/reconciler engines, per-tool
// capability scores, the 11-value tool-type universe) and adapts it into a flat
// view model the report page renders.
//
// We scan THREE component kinds — MCP servers, skills, and agentic code — so the
// component list mixes `mcp-server` and `skill` (and future) types; the report
// is component-type-agnostic and drives everything off the rollups.

// ── Raw shapes (as emitted; every field optional-tolerant) ──────────────────

export type Verdict = "VULNERABLE" | "SECURE" | "UNKNOWN" | "NOT_APPLICABLE";
export type Severity = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
export type ToolKind =
  | "read-only"
  | "write"
  | "destructive"
  | "exec-capable"
  | "secrets-access"
  | "admin-config"
  | "cost-impact"
  | "iam-admin"
  | "network-broad"
  | "untrusted-output"
  | "unknown";

export interface RawEvidence {
  path: string;
  start_line: number;
  end_line: number;
  snippet: string;
  match_type: string;
}
export interface RawFinding {
  rule_id: string;
  rule_name: string;
  rule_version?: string;
  finding: string; // VULNERABLE | SECURE | UNKNOWN | NOT_APPLICABLE
  severity: string;
  realized_severity?: string;
  confidence: string;
  component_name?: string;
  evidence?: RawEvidence[] | null;
  why_this_matters?: string;
  fix_guidance?: string;
  rationale?: string;
  vulnerability_details?: string;
  false_positive_check?: string;
  mitigation_present?: boolean;
  within_declared_contract?: boolean;
  transport_gated?: boolean;
  affected_tools?: string[] | null;
  reconciliation_note?: string;
  context_note?: string;
}
export interface RawTool {
  tool_name: string;
  tool_description: string;
  types: string[];
  status: string; // IMPLEMENTED | NOT_IMPLEMENTED
  component_name?: string;
}
export interface RawScoreBlock {
  score: number;
  tier: string;
  capability_band?: string;
  score_confidence?: string;
  score_interval?: { low: number; high: number };
  blocked_reason?: string | null;
  factors?: string[];
}
export interface RawToolScore {
  tool_name: string;
  score: number;
  tier: string;
  capability_band?: string;
  score_confidence?: string;
  reasons?: string[];
}
export interface RawArtifactPayload {
  artifact_id: string;
  component: {
    component_id: string;
    component_name: string;
    component_path: string;
    component_type: string; // mcp-server | skill | ...
    metadata?: {
      transports?: string[];
      server_info_name?: string | null;
      server_info_version?: string | null;
    };
  };
  component_findings?: RawFinding[] | null;
  repo_findings_inherited?: RawFinding[] | null;
  component_tools?: RawTool[] | null;
  scan_provenance?: { resolved_ref?: string | null; scanned_at?: string | null };
  repo_context?: {
    repo_url?: string;
    languages?: string[];
    transports_detected?: string[];
    deployment_artifacts?: string[];
  };
  rollup?: {
    component_findings_count?: Record<string, number>;
    repo_findings_inherited_count?: Record<string, number>;
    component_tools_count?: Record<string, number>;
    worst_case_severity?: string;
  };
  trust_scores?: {
    repo_level?: RawScoreBlock;
    component_level?: RawScoreBlock;
    tool_level?: RawToolScore[];
  };
  summary?: { high_level_risks?: string[] | null; top_remediations?: string[] | null };
}
// The zeus scan envelope wraps each artifact in `payload`; the console's stored
// ScanResult (GET /scans/{id}) returns the same fields UN-wrapped. Accept both —
// the adapter normalises via `pod()`.
export type RawArtifact = { payload?: RawArtifactPayload } & Partial<RawArtifactPayload>;
export interface RawStage {
  started_at?: string;
  finished_at?: string;
  status?: string;
}
export interface RawScanReport {
  scan_id: string;
  overall_status?: string;
  status?: string; // the console ScanResult uses `status` (pending|running|done|error)
  worst_severity?: string; // top-level on the console ScanResult (no scan_summary block)
  passed?: boolean;
  server?: string;
  scanned_at?: string;
  created_at?: string;
  cost_usd?: { scan?: number; adjudication?: number; reconciliation?: number; total?: number };
  tenant?: { id?: string; is_new?: boolean };
  engines_used?: Record<string, { engine?: string; compute?: string; model?: string; rules_hash?: string | null }>;
  worker_versions?: Record<string, { commit?: string; version?: string }>;
  callback_status?: string | { status?: string; target?: string } | null;
  eval_pack?: { commit_sha?: string; hash?: string; label?: string | null; source?: string | null };
  scan_summary?: {
    total_components?: number;
    worst_case_severity?: string;
    repo_trust_score?: { score?: number; tier?: string };
  };
  stages?: Record<string, RawStage>;
  artifacts?: RawArtifact[];
}

// ── View model (what the report page consumes) ──────────────────────────────

export interface VMFinding extends RawFinding {
  verdict: Verdict;
}
export interface VMToolRow extends RawTool {
  kinds: ToolKind[];
  score: number | null;
  tier: string | null;
  band: string | null;
}
export interface VMComponent {
  id: string;
  name: string;
  path: string;
  type: string;
  serverName?: string | null;
  version?: string | null;
  transports: string[];
  findingsCount: number;
  toolsCount: number;
  trustScore: number | null;
  trustTier: string | null;
  findings: VMFinding[];
  tools: VMToolRow[];
  toolTypeCounts: Record<string, number>;
  worstSeverity?: string;
}
export interface VMLifecycle {
  segments: { key: string; label: string; ms: number }[];
  totalMs: number;
}
export interface ScanReportVM {
  scanId: string;
  status: string;
  createdAt?: string;
  completedAt?: string;
  cost: { scan: number; adjudication: number; reconciliation: number; total: number };
  tenant: { id?: string; isNew?: boolean };
  engines: { role: string; model: string; engine?: string }[];
  workerVersions: { name: string; version?: string; commit?: string }[];
  callback: { status: string; target?: string };
  evalPack: { source?: string | null; label?: string | null; sha?: string };
  // summary
  repoUrl?: string;
  ref?: string; // scanned repo commit (resolved_ref), falling back to eval-pack sha
  rulesetSha?: string; // the adjudication ruleset commit (eval_pack) — shown under Evaluation
  languages: string[];
  transports: string[];
  totalComponents: number;
  worstSeverity?: string;
  repoTrust: { score?: number; tier?: string };
  // repo-level narrative + findings (dedup: read once)
  highLevelRisks: string[];
  topRemediations: string[];
  globalFindings: VMFinding[];
  globalFindingCounts: { vulnerable: number; secure: number; unknown: number; notApplicable: number };
  // components
  components: VMComponent[];
  lifecycle: VMLifecycle;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const asVerdict = (f: string): Verdict => {
  const v = (f || "").toUpperCase();
  if (v === "VULNERABLE") return "VULNERABLE";
  if (v === "SECURE") return "SECURE";
  if (v === "NOT_APPLICABLE" || v === "NOT-APPLICABLE" || v === "N/A") return "NOT_APPLICABLE";
  return "UNKNOWN";
};
const asKind = (t: string): ToolKind => {
  const k = (t || "").toLowerCase() as ToolKind;
  const all: ToolKind[] = [
    "read-only", "write", "destructive", "exec-capable", "secrets-access",
    "admin-config", "cost-impact", "iam-admin", "network-broad", "untrusted-output", "unknown",
  ];
  return all.includes(k) ? k : "unknown";
};
const ms = (a?: string, b?: string): number => {
  if (!a || !b) return 0;
  const x = new Date(a).getTime();
  const y = new Date(b).getTime();
  return Number.isFinite(x) && Number.isFinite(y) && y >= x ? y - x : 0;
};

export function fmtDuration(msVal: number): string {
  if (!msVal) return "—";
  const s = Math.round(msVal / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  parts.push(`${sec}s`);
  return parts.join(" ");
}
export function fmtUsd(n?: number): string {
  if (n == null) return "—";
  return `$${n.toFixed(2)}`;
}

// Semantic tone (drives a Tailwind text-* / bg-* class, never a hex).
export type Tone = "danger" | "warning" | "success" | "info" | "governance" | "muted";
// Narrowed to the tones a severity can actually take (never "success"), which is
// also exactly the subset agate's SeverityText accepts.
export function severityToTone(sev?: string): "danger" | "warning" | "info" | "muted" {
  const s = (sev || "").toUpperCase();
  if (s === "CRITICAL" || s === "HIGH") return "danger";
  if (s === "MEDIUM") return "warning";
  if (s === "LOW") return "info";
  return "muted";
}
export function verdictToTone(v?: string): Tone {
  const s = (v || "").toUpperCase();
  if (s === "VULNERABLE") return "danger";
  if (s === "SECURE") return "success";
  return "muted";
}
export function tierToTone(tier?: string): Tone {
  const t = (tier || "").toLowerCase();
  if (t === "critical") return "danger";
  if (t === "low") return "warning";
  if (t === "medium") return "info";
  if (t === "high") return "success";
  return "muted";
}
// Capability tone for a tool kind: destructive/exec/secrets/iam/network = hot,
// write/admin/cost = warm, read-only = cool, else neutral.
export function toolKindTone(kind: string): Tone {
  const k = (kind || "").toLowerCase();
  if (["destructive", "exec-capable", "secrets-access", "iam-admin", "network-broad", "untrusted-output"].includes(k))
    return "danger";
  if (["write", "admin-config", "cost-impact"].includes(k)) return "warning";
  if (k === "read-only") return "success";
  return "muted";
}

// Ordered tool-type universe for the count chips (hottest capability first).
// Each carries a plain-language gloss for the hover hint — these capability
// names are the report's core value and must not ship undefined.
export const TOOL_KIND_ORDER: { key: string; label: string; countKey: string; hint: string }[] = [
  { key: "read-only", label: "read-only", countKey: "read_only", hint: "Only reads data; makes no changes." },
  { key: "write", label: "write", countKey: "write", hint: "Creates or modifies data." },
  { key: "destructive", label: "destructive", countKey: "destructive", hint: "Can delete data or cause irreversible changes." },
  { key: "exec-capable", label: "exec-capable", countKey: "exec_capable", hint: "Can execute code or shell commands." },
  { key: "secrets-access", label: "secrets-access", countKey: "secrets_access", hint: "Can read credentials, tokens, or other secrets." },
  { key: "admin-config", label: "admin-config", countKey: "admin_config", hint: "Can change administrative or security configuration." },
  { key: "cost-impact", label: "cost-impact", countKey: "cost_impact", hint: "Can incur cloud or API spend." },
  { key: "iam-admin", label: "iam-admin", countKey: "iam_admin", hint: "Can change identity, roles, or access policy." },
  { key: "network-broad", label: "network-broad", countKey: "network_broad", hint: "Can reach arbitrary network destinations." },
  { key: "untrusted-output", label: "untrusted-output", countKey: "untrusted_output", hint: "Returns third-party content that must not be trusted as instructions." },
];

// Gloss lookup for a single capability chip (used in the tools table rows).
export const TOOL_KIND_HINT: Record<string, string> = Object.fromEntries(
  TOOL_KIND_ORDER.map((t) => [t.key, t.hint]),
);

// ── Live fetch ──────────────────────────────────────────────────────────────
// GET /scans/{id} returns the stored ScanResult (api/internal/mcpscan). Its shape
// is a superset-tolerant match for RawScanReport: artifacts arrive un-wrapped and
// the zeus top-level block (stages/cost/engines/…) is absent — the adapter derives
// what it can and the report hides the cards it can't fill.
export async function fetchScanReport(id: string): Promise<RawScanReport> {
  const base = import.meta.env.BASE_URL + "api/v1";
  const tok = localStorage.getItem("bluerock.token");
  const res = await fetch(`${base}/scans/${encodeURIComponent(id)}`, {
    cache: "no-store",
    headers: tok ? { Authorization: `Bearer ${tok}` } : {},
  });
  if (!res.ok) throw new Error(`Could not load scan ${id} (${res.status})`);
  return (await res.json()) as RawScanReport;
}

// ── The adapter ─────────────────────────────────────────────────────────────

export function adaptScanReport(raw: RawScanReport): ScanReportVM {
  // The zeus envelope wraps each artifact in `payload`; the console's stored
  // ScanResult returns the fields un-wrapped. Normalise to the inner payload.
  const pod = (a: RawArtifact): RawArtifactPayload => (a?.payload ?? (a as RawArtifactPayload));
  // Drop malformed artifacts (a null `component` would otherwise throw below and
  // blank the whole report).
  const arts = (raw.artifacts ?? []).filter((a) => pod(a)?.component);

  // Repo-level views (findings, risks, remediations, repo_context) are duplicated
  // across every artifact, but reading artifacts[0] positionally is unsafe — if the
  // backend ever emits skills first, or an artifact[0] with empty inherited
  // findings, we'd render a false "no findings". Pick deterministically: prefer the
  // mcp-server, else the first artifact that actually carries repo-level findings.
  const first =
    arts.map(pod).find((p) => p.component.component_type === "mcp-server") ??
    arts.map(pod).find((p) => (p.repo_findings_inherited?.length ?? 0) > 0) ??
    (arts[0] ? pod(arts[0]) : undefined);
  // The scanned repo's own commit lives on the artifact provenance; the eval-pack
  // sha is the *ruleset* commit and is shown separately under Evaluation.
  const resolvedRef = arts.map(pod).find((p) => p.scan_provenance?.resolved_ref)?.scan_provenance?.resolved_ref;

  // Repo-level views are duplicated across every artifact — read once.
  const globalFindings: VMFinding[] = (first?.repo_findings_inherited ?? []).map((f) => ({
    ...f,
    verdict: asVerdict(f.finding),
  }));
  const gc = { vulnerable: 0, secure: 0, unknown: 0, notApplicable: 0 };
  for (const f of globalFindings) {
    if (f.verdict === "VULNERABLE") gc.vulnerable++;
    else if (f.verdict === "SECURE") gc.secure++;
    else if (f.verdict === "NOT_APPLICABLE") gc.notApplicable++;
    else gc.unknown++;
  }

  const components: VMComponent[] = arts.map((a) => {
    const p = pod(a);
    const toolScores = new Map<string, RawToolScore>();
    for (const ts of p.trust_scores?.tool_level ?? []) toolScores.set(ts.tool_name, ts);
    const tools: VMToolRow[] = (p.component_tools ?? []).map((t) => {
      const sc = toolScores.get(t.tool_name);
      return {
        ...t,
        kinds: (t.types ?? []).map(asKind),
        score: sc?.score ?? null,
        tier: sc?.tier ?? null,
        band: sc?.capability_band ?? null,
      };
    });
    const findings: VMFinding[] = (p.component_findings ?? []).map((f) => ({
      ...f,
      verdict: asVerdict(f.finding),
    }));
    return {
      id: p.component.component_id,
      name: p.component.component_name || p.component.component_path,
      path: p.component.component_path,
      type: p.component.component_type,
      serverName: p.component.metadata?.server_info_name,
      version: p.component.metadata?.server_info_version,
      transports: p.component.metadata?.transports ?? [],
      findingsCount: findings.length,
      toolsCount: tools.length,
      trustScore: p.trust_scores?.component_level?.score ?? null,
      trustTier: p.trust_scores?.component_level?.tier ?? null,
      findings,
      tools,
      toolTypeCounts: p.rollup?.component_tools_count ?? {},
      worstSeverity: p.rollup?.worst_case_severity,
    };
  });

  const stages = raw.stages ?? {};
  const lifecycle: VMLifecycle = (() => {
    const scanMs = ms(stages.scan?.started_at, stages.scan?.finished_at);
    const adjMs = ms(stages.adjudication?.started_at, stages.adjudication?.finished_at);
    const recMs = ms(stages.reconciliation?.started_at, stages.reconciliation?.finished_at);
    const queuedMs = ms(raw.created_at, stages.scan?.started_at);
    const segments = [
      { key: "queued", label: "queued", ms: queuedMs },
      { key: "scanning", label: "scanning", ms: scanMs },
      { key: "adjudicating", label: "adjudicating", ms: adjMs },
      { key: "reconciling", label: "reconciling", ms: recMs },
    ].filter((s) => s.ms > 0);
    return { segments, totalMs: segments.reduce((a, s) => a + s.ms, 0) };
  })();

  const eng = raw.engines_used ?? {};
  const engines = [
    { role: "primary", model: eng.scan?.model ?? "—", engine: eng.scan?.engine },
    { role: "adjudicator", model: eng.adjudicator?.model ?? "—", engine: eng.adjudicator?.engine },
    { role: "reconciler", model: eng.reconciler?.model ?? "—", engine: eng.reconciler?.engine },
  ].filter((e) => e.model !== "—");

  const cb = raw.callback_status;
  const callback =
    typeof cb === "string"
      ? { status: cb }
      : { status: cb?.status ?? "not_required", target: cb?.target };

  return {
    scanId: raw.scan_id,
    status: raw.overall_status ?? raw.status ?? "",
    createdAt: raw.created_at,
    completedAt: raw.scanned_at || first?.scan_provenance?.scanned_at || undefined,
    cost: {
      scan: raw.cost_usd?.scan ?? 0,
      adjudication: raw.cost_usd?.adjudication ?? 0,
      reconciliation: raw.cost_usd?.reconciliation ?? 0,
      total: raw.cost_usd?.total ?? 0,
    },
    tenant: { id: raw.tenant?.id, isNew: raw.tenant?.is_new },
    engines,
    workerVersions: Object.entries(raw.worker_versions ?? {}).map(([name, v]) => ({
      name,
      version: v.version,
      commit: v.commit,
    })),
    callback,
    evalPack: { source: raw.eval_pack?.source, label: raw.eval_pack?.label, sha: raw.eval_pack?.commit_sha },
    repoUrl: first?.repo_context?.repo_url,
    ref: resolvedRef || raw.eval_pack?.commit_sha,
    rulesetSha: raw.eval_pack?.commit_sha,
    languages: first?.repo_context?.languages ?? [],
    transports: first?.repo_context?.transports_detected ?? [],
    totalComponents: raw.scan_summary?.total_components ?? components.length,
    // Repo trust: prefer the zeus top-level summary; else derive from the repo-level
    // trust score the console carries on the artifact.
    worstSeverity: raw.scan_summary?.worst_case_severity ?? raw.worst_severity,
    repoTrust: {
      score: raw.scan_summary?.repo_trust_score?.score ?? first?.trust_scores?.repo_level?.score,
      tier: raw.scan_summary?.repo_trust_score?.tier ?? first?.trust_scores?.repo_level?.tier,
    },
    highLevelRisks: first?.summary?.high_level_risks ?? [],
    topRemediations: first?.summary?.top_remediations ?? [],
    globalFindings,
    globalFindingCounts: gc,
    components,
    lifecycle,
  };
}
